import './StatsChart.sass';

import { FC, useEffect, useMemo, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import format from 'date-fns/format';
import { fromUnixTime } from 'date-fns';

import { useTranslator } from '$hooks';

import type { LinkMeta } from '../StatisticsProLinks.api';

export interface LinksChartProps {
  days: number[];
  values: Record<number, Record<number, number>>; // day -> linkId -> joins
  links: LinkMeta[];
}

const countFormatter = new Intl.NumberFormat('en-US');

const PALETTE = [
  '#3478f6',
  '#34c759',
  '#ff9f0a',
  '#ff375f',
  '#bf5af2',
  '#64d2ff',
  '#ffd60a',
  '#ac8e68',
];

export const LinksChart: FC<LinksChartProps> = ({ days, values, links }) => {
  const t = useTranslator();
  const daysKey = days.length
    ? `${days[0]}_${days[days.length - 1]}_${days.length}`
    : 'empty';
  const prevDaysKeyRef = useRef<string | null>(null);
  const shouldAnimate = prevDaysKeyRef.current !== daysKey;
  useEffect(() => {
    prevDaysKeyRef.current = daysKey;
  }, [daysKey]);

  const total = useMemo(() => {
    let sum = 0;
    for (const d of days) {
      const dayValues = values[d];
      if (!dayValues) continue;
      for (const v of Object.values(dayValues)) {
        sum += v ?? 0;
      }
    }
    return sum;
  }, [days, values]);

  const options: Highcharts.Options = useMemo(() => {
    const xCategories = days.map((d) => format(fromUnixTime(d), 'd MMM'));

    const series: Highcharts.SeriesOptionsType[] = links.map((link, index) => ({
      type: 'spline',
      name: link.name,
      data: days.map((d) => values[d]?.[link.id] ?? 0),
      color: PALETTE[index % PALETTE.length],
    }));

    return {
      title: { text: '' },
      chart: {
        type: 'spline',
        backgroundColor: 'transparent',
        height: 320,
        spacing: [8, 8, 8, 8],
        style: { fontFamily: "'Nunito Sans', sans-serif" },
      },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories: xCategories,
        labels: { style: { color: '#9b9ba0' } },
        lineColor: 'rgba(255,255,255,0.06)',
        tickColor: 'rgba(255,255,255,0.06)',
        tickInterval: Math.max(1, Math.floor(xCategories.length / 8)),
      },
      yAxis: {
        title: { text: '' },
        labels: { enabled: false },
        gridLineColor: 'rgba(255,255,255,0.05)',
        allowDecimals: false,
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: '#1c1c1f',
        borderColor: 'rgba(255,255,255,0.1)',
        style: { color: '#fff' },
        formatter() {
          const point = this as Highcharts.TooltipFormatterContextObject;
          const MAX_ROWS = 8;
          const nonZero = (point.points ?? [])
            .filter((p) => Number(p.y ?? 0) > 0)
            .sort((a, b) => Number(b.y ?? 0) - Number(a.y ?? 0));
          const shown = nonZero.slice(0, MAX_ROWS);
          const othersSum = nonZero
            .slice(MAX_ROWS)
            .reduce((acc, p) => acc + Number(p.y ?? 0), 0);
          let rows = shown
            .map(
              (p) =>
                `<div style="display:flex;justify-content:space-between;gap:12px;">
                   <span><span style="color:${p.color};">●</span> ${p.series.name}</span>
                   <b>${countFormatter.format(Number(p.y ?? 0))}</b>
                 </div>`,
            )
            .join('');
          if (othersSum > 0) {
            rows += `<div style="display:flex;justify-content:space-between;gap:12px;">
                   <span><span style="color:#9b9ba0;">●</span> ${t(
                     'statistics_pro_others',
                   )}</span>
                   <b>${countFormatter.format(othersSum)}</b>
                 </div>`;
          }
          const dayTotal = (point.points ?? []).reduce(
            (acc, p) => acc + Number(p.y ?? 0),
            0,
          );
          return `<div style="font-size:12px;min-width:180px;">
            <div style="margin-bottom:6px;opacity:0.7;">${point.x}</div>
            ${rows}
            <div style="margin-top:6px;border-top:1px solid rgba(255,255,255,0.08);padding-top:4px;display:flex;justify-content:space-between;">
              <span>${t('statistics_pro_total')}</span><b>${countFormatter.format(
            dayTotal,
          )}</b>
            </div>
          </div>`;
        },
      },
      plotOptions: {
        series: {
          animation: shouldAnimate ? { duration: 900 } : false,
          marker: { enabled: false, symbol: 'circle' },
          states: { inactive: { opacity: 0.4 } },
        },
      },
      series,
    };
  }, [days, values, links, t, shouldAnimate]);

  const isEmpty = links.length === 0;

  return (
    <div className="StatsChart">
      <div className="StatsChart__plot">
        <div className="StatsChart__header">
          <div className="StatsChart__title">
            {t('statistics_pro_links_joined', { defaultValue: 'Joined in period' })}
          </div>
          <div className="StatsChart__title">{countFormatter.format(total)}</div>
        </div>
        {isEmpty ? (
          <div className="StatsChart__empty">{t('statistics_pro_no_data')}</div>
        ) : (
          <HighchartsReact
            highcharts={Highcharts}
            options={options}
            allowChartUpdate
            immutable
          />
        )}
      </div>
    </div>
  );
};
